export interface MessageContent {
    encryptedKey: string
    iv: string
    cipherText: string
}

export class Encryptor {
    async encrypt(message: string): Promise<MessageContent> {
        return await this.hybridEncrypt(
            message,
            await this.getRsaPublicKeyAsCryptoKey(),
        );
    }

    private async hybridEncrypt(message: string, rsaKey: CryptoKey): Promise<MessageContent> {
        // 1. Générer clé AES-GCM
        const aesKey = await crypto.subtle.generateKey(
            { name: "AES-GCM", length: 256 },
            true,
            ["encrypt", "decrypt"],
        );

        // Generate iv
        const iv = crypto.getRandomValues(new Uint8Array(12));

        // 2. Chiffrer le message avec AES
        const encodedMessage = new TextEncoder().encode(message);
        const cipherText = await crypto.subtle.encrypt(
            { name: "AES-GCM", iv },
            aesKey,
            encodedMessage,
        );

        // 3. Exporter la clé AES en binaire
        const rawAesKey = await crypto.subtle.exportKey("raw", aesKey);

        // 4. Chiffrer la clé AES avec RSA
        const encryptedKey = await crypto.subtle.encrypt(
            { name: "RSA-OAEP" },
            rsaKey,
            rawAesKey,
        );

        return {
            encryptedKey: this.arrayBufferToBase64(encryptedKey),
            iv: this.bytesToBase64(iv),
            cipherText: this.arrayBufferToBase64(cipherText),
        };
    }

    /**
     * Retrieve the RSA public key file content. This action is asynchronous.
     */
    async getRsaPublicKeyFileContent(url: string): Promise<string> {
        const res = await fetch(url, { cache: "no-cache" });
        if (!res.ok) throw new Error(`Failed to fetch PEM: ${res.status}`);
        return await res.text();
    }

    /**
     * Retrieve the RSA public key as CryptoKey object.
     */
    async getRsaPublicKeyAsCryptoKey(): Promise<CryptoKey> {
        const pem = await this.getRsaPublicKeyFileContent('/storage/keys/public.pem');

        const b64 = pem
            .replace(/-----.*-----/g, "")
            .replace(/\s+/g, "");
        const binary = Uint8Array.from(atob(b64), c => c.charCodeAt(0));

        return await crypto.subtle.importKey(
            "spki",
            binary.buffer,
            { name: "RSA-OAEP", hash: "SHA-1" },
            false,
            ["encrypt"],
        );
    }

    /**
     * Convert an array buffer object (binary content) to a base64 string (this ensure the integrity of the text content)
     */
    private arrayBufferToBase64(buffer: ArrayBuffer): string {
        const bytes = new Uint8Array(buffer);
        return this.bytesToBase64(bytes);
    }

    /**
     * Convert bytes to a base64 string (this ensure the integrity of the text content).
     */
    private bytesToBase64(bytes: Uint8Array<ArrayBuffer>): string {
        let binary = '';
        const chunkSize = 0x8000;
        for (let i = 0; i < bytes.length; i += chunkSize) {
            const chunk = bytes.subarray(i, i + chunkSize);
            binary += String.fromCharCode(...chunk);
        }
        return btoa(binary);
    }
}
